import React from "react";

import classNames from "classnames";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, inputClasses } from "./styles";

import type { JSX } from "react";

import type { InputProps } from "./types";

export default function ColorPickerInput({
  isRequired,
  label,
  name,
  sublabel,
  defaultValue,
  isReadOnly,
  isFullWidth,
  isNoWrap,
  isCompact,
  className,
}: InputProps): JSX.Element {
  const {
    control,
    getFieldState,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("editor-errors");

  const isInvalid = getFieldState(name)?.invalid;
  const error = get(errors, name);

  const colorPickerWrapperClassNames = classNames(
    "relative",
    isFullWidth ? "flex-1" : "flex-grow-0",
  );

  const fieldContainerClassNames = classNames({
    "!flex-col": isCompact,
  });

  return (
    <FieldContainer
      className={className}
      containerClassName={fieldContainerClassNames}
      isCompact={isCompact}
      isNoWrap={isNoWrap}
    >
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className={colorPickerWrapperClassNames}>
        <div
          className={containerClasses({
            isColorPicker: true,
            isFullWidth,
          })}
        >
          <Controller
            control={control}
            name={name}
            render={({ field }): JSX.Element => (
              <React.Fragment>
                <div className="absolute inset-y-0 left-0 flex w-[1.875rem] items-center justify-center border-r pb-[2px]">
                  <input
                    {...field} // eslint-disable-line react/jsx-props-no-spreading
                    className="absolute size-full cursor-pointer opacity-0"
                    defaultValue={defaultValue}
                    disabled={isReadOnly}
                    type="color"
                  />
                  <div
                    className="pointer-events-none mt-[0.15625rem] ml-[0.15625rem] size-[1.25rem] rounded-[0.3125rem]"
                    style={{ backgroundColor: field.value }}
                  />
                </div>
                <input
                  {...field} // eslint-disable-line react/jsx-props-no-spreading
                  defaultValue={defaultValue}
                  disabled={isReadOnly}
                  maxLength={7}
                  type="text"
                  className={inputClasses({
                    isColorpicker: true,
                    isInvalid,
                    isReadOnly,
                  })}
                />
              </React.Fragment>
            )}
            rules={{
              required: isRequired && t("requiredField"),
              pattern: {
                value: /^#(?:[0-9a-fA-F]{3}){1,2}$/,
                message: t("colorPickerField"),
              },
            }}
          />
        </div>
        <ErrorMessage error={error} />
      </div>
    </FieldContainer>
  );
}
